import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import FactionMpProvider from "@/core/data-provider/data-mp/faction/faction-mp-provider";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import toast from "@/domains/toasts/helpers/toast";
import { QUERY_KEY_FACTION_GENERAL_INFO } from "@/domains/faction/hooks/useGetInfo";
import { QUERY_KEY_FACTION_COLLEAGUES_INFO } from "@/domains/faction/hooks/useGetParticipants";

type KnownErr = Error & { knownI18nKey?: boolean };
type Vars = { characterId: number; reason: string; cb?: () => void };

export function useAddInBlackList() {
  const { t, i18n } = useTranslation("faction");
  const queryClient = useQueryClient();

  return useMutation<void, KnownErr, Vars>({
    mutationFn: async ({ characterId, reason }) => {
      const response = await FactionMpProvider.addToBlacklist(characterId, reason);

      if (response instanceof RpcMpProviderFailedResponse) {
        const messageKey = response?.error?.message ?? "Unknown";
        const knownI18nKey = i18n.exists(`faction:faction.errors.${messageKey}`);
        const error = new Error(messageKey) as KnownErr;
        error.knownI18nKey = knownI18nKey;
        throw error;
      }
    },
    onSuccess: async (_data, variables) => {
      toast.success(t("faction.toast.addToBlackListSuccess"));
      variables?.cb?.();
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FACTION_GENERAL_INFO] }),
        queryClient.invalidateQueries({ queryKey: [QUERY_KEY_FACTION_COLLEAGUES_INFO] }),
      ]);
    },
    onError: (err) => {
      const error = err as KnownErr;
      console.error(error);
      if (error.knownI18nKey) {
        toast.error(t(`faction.errors.${error.message}`));
      } else {
        toast.error(t("faction.toast.addToBlackListError"));
      }
    },
  });
}
